import type {
  AcLineSegmentObject,
  LoadFlowScenarioElementCalculation,
  LoadFlowScenarioResults
} from '../../../..';
import type { AcLineEvaluatedLoading } from '../../../../derived-state/loadFlowSummary';
import type { ForStoringAttentionStatus } from '../../../AttentionStatusRepository';
import {
  selectByType,
  selectByTypeFromAllScenarios
} from '../../../selectByType';
import { AcLineSegmentSummaryEvaluation } from './acLineSegmentSummaryEvaluation';
import type { ForAcLineSegmentEvaluation } from './forAcLineSegmentEvaluation';

export class AcLineSegmentEvaluation {
  private constructor(
    private readonly acLineSegments: AcLineSegmentObject[],
    private readonly evaluationStatusRepository: ForStoringAttentionStatus
  ) {
    this.acLineSegments = acLineSegments;
    this.evaluationStatusRepository = evaluationStatusRepository;
  }

  static from(
    loadFlowScenarios: LoadFlowScenarioResults[],
    evaluationEventHandler: ForStoringAttentionStatus
  ) {
    const results = selectByTypeFromAllScenarios(
      'AcLineSegment',
      loadFlowScenarios
    );
    return new AcLineSegmentEvaluation(results, evaluationEventHandler);
  }

  static fromResults(
    loadFlowResults: LoadFlowScenarioElementCalculation[],
    evaluationEventHandler: ForStoringAttentionStatus
  ) {
    const results = selectByType('AcLineSegment', loadFlowResults);
    return new AcLineSegmentEvaluation(results, evaluationEventHandler);
  }

  private get acLineSegmentResults() {
    return this.acLineSegments.map(acLineSegment => acLineSegment.result);
  }

  evaluateSummary(
    evaluationEngine: ForAcLineSegmentEvaluation
  ): AcLineSegmentEvaluationResult {
    const summaryEvaluation = new AcLineSegmentSummaryEvaluation(
      this.acLineSegmentResults,
      evaluationEngine
    );

    const loadingResult = summaryEvaluation.evaluateLoadingPercents();
    const attentionRequired = isAttentionRequired(loadingResult);

    return { loadingResult, attentionRequired };
  }

  evaluateEachLine(evaluationEngine: ForAcLineSegmentEvaluation) {
    return this.acLineSegments.map(acLineSegment => {
      return this.evaluateAcLine(evaluationEngine, acLineSegment);
    });
  }

  private evaluateAcLine(
    evaluationEngine: ForAcLineSegmentEvaluation,
    acLineSegment: AcLineSegmentObject
  ) {
    const messenger = this.evaluationStatusRepository.delegateMessenger(
      acLineSegment.id,
      acLineSegment.objectType
    );
    const loadingPercent = evaluationEngine.evaluateLoadingPercent(
      acLineSegment.result.loadingPercent,
      messenger
    );
    const current = evaluationEngine.evaluateCurrent(
      acLineSegment.result.current,
      messenger
    );

    const availableCapacity = evaluationEngine.evaluateAvailableCapacity(
      acLineSegment.result.availableCapacity,
      messenger
    );

    return {
      id: acLineSegment.id,
      objectType: acLineSegment.objectType,
      results: {
        loadingPercent,
        current,
        availableCapacity
      }
    };
  }
}

export type AcLineSegmentEvaluationResult = {
  loadingResult: AcLineEvaluatedLoading;
  attentionRequired: boolean;
};

function isAttentionRequired(loadingResult: AcLineEvaluatedLoading) {
  return (
    loadingResult.max.status === 'ATTENTION_REQUIRED' ||
    loadingResult.min.status === 'ATTENTION_REQUIRED'
  );
}
